import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  AdminConversationHistoryPage,
  AdminConversationsPage,
  AdminConversationsParams,
  getAdminConversationHistory,
  getAdminConversations,
} from "@src/apis/adminCuratorConversationApis";
import { QUERY_KEYS } from "./queryKeys";

export const ADMIN_CONVERSATIONS_PAGE_SIZE = 25;
export const ADMIN_HISTORY_PAGE_SIZE = 50;

export const useAdminConversationsQuery = (
  params: AdminConversationsParams,
  { enabled = true }: { enabled?: boolean } = {}
) =>
  useQuery<AdminConversationsPage, Error>({
    queryKey: [QUERY_KEYS.ADMIN_CONVERSATIONS, params],
    queryFn: () => getAdminConversations(params),
    enabled,
    // A moderation view is only useful when it is current, so it always refetches
    // on mount instead of serving the default staleTime from cache.
    staleTime: 0,
    refetchOnMount: "always",
  });

/**
 * The transcript, newest page first. Each fetched page walks further back through
 * history via the opaque `nextCursor` the backend hands out, so "Load older"
 * appends an older page instead of re-reading the whole conversation.
 */
export const useAdminConversationHistory = (conversationId: string | null) =>
  useInfiniteQuery<AdminConversationHistoryPage, Error>({
    queryKey: [QUERY_KEYS.ADMIN_CONVERSATION_HISTORY, conversationId],
    queryFn: ({ pageParam }) =>
      getAdminConversationHistory(conversationId as string, {
        pageSize: ADMIN_HISTORY_PAGE_SIZE,
        before: (pageParam as string) || undefined,
      }),
    initialPageParam: "",
    getNextPageParam: (lastPage) =>
      lastPage.hasMore && lastPage.nextCursor ? lastPage.nextCursor : undefined,
    enabled: Boolean(conversationId),
    staleTime: 0,
    refetchOnMount: "always",
  });
